import { useRef, useState } from "react";
import { DateTime } from "luxon";
import { AlarmClock, AlarmClockOff, Star } from "lucide-react";
import type { TaskWithFlags } from "@desk/contracts";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@/components/ui/misc";
import { MonthGrid } from "@/components/ui/date-picker";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useTaskActions } from "@/data/actions";
import { useHierarchy } from "@/data/hooks";
import { addDays, customInstant, formatTime, nowIn, rowSnoozeOptions, toIsoUtc } from "@/lib/dates";
import { cn } from "@/lib/utils";

/** Resolved time beside a menu label: "6:30 PM" today, otherwise "Fri 9 AM" (the label already says which day). */
function menuTime(at: DateTime, now: DateTime): string {
  const time = formatTime(at, { short: true });
  if (at.hasSame(now, "day")) return time;
  return `${at.toFormat(at.diff(now, "days").days < 6 ? "ccc" : "LLL d")} ${time}`;
}

/**
 * The row's clock menu: Later today, Tomorrow, This weekend, Next week (each with its resolved time),
 * then Pick a day…, which opens a month popover at the same spot and snoozes until 9 AM that day.
 */
export function RowSnoozeMenu({ task, className }: { task: TaskWithFlags; className?: string }) {
  const a = useTaskActions();
  const h = useHierarchy();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const picking = useRef(false);
  const options = menuOpen ? rowSnoozeOptions(h.timezone) : [];
  // The earliest pickable day is today while 9 AM is still ahead, otherwise tomorrow.
  const now = nowIn(h.timezone);
  const minDay = now < customInstant(h.today, "09:00", h.timezone) ? h.today : addDays(h.today, 1);
  return (
    <Popover open={pickOpen} onOpenChange={setPickOpen}>
      <DropdownMenu modal={false} open={menuOpen} onOpenChange={setMenuOpen}>
        <Tooltip content="Snooze" shortcut="H">
          <PopoverAnchor asChild>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon-sm"
                variant="quiet"
                aria-label={`Snooze ${task.title}`}
                data-state={menuOpen || pickOpen ? "open" : "closed"}
                className={cn("data-[state=open]:bg-hover data-[state=open]:text-foreground", className)}
              >
                <AlarmClock />
              </Button>
            </DropdownMenuTrigger>
          </PopoverAnchor>
        </Tooltip>
        <DropdownMenuContent
          align="end"
          className="min-w-[232px]"
          onKeyDown={(e) => e.stopPropagation()}
          onCloseAutoFocus={(e) => {
            if (picking.current) e.preventDefault();
          }}
        >
          {options.map((o) => (
            <DropdownMenuItem key={o.key} onSelect={() => void a.snooze(task, toIsoUtc(o.at))}>
              <span>{o.label}</span>
              <span className="ml-auto pl-6 text-[12px] text-subtle tabular">{menuTime(o.at, now)}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => {
              picking.current = true;
              // Open after the menu has closed so its dismissal doesn't close the popover too.
              setTimeout(() => {
                setPickOpen(true);
                picking.current = false;
              }, 0);
            }}
          >
            Pick a day…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <PopoverContent align="end" className="p-2" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <MonthGrid
          value={null}
          today={h.today}
          min={minDay}
          onSelect={(day) => {
            const at = customInstant(day, "09:00", h.timezone);
            if (!at.isValid || at <= DateTime.now()) return;
            setPickOpen(false);
            void a.snooze(task, toIsoUtc(at));
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** Hover/focus quick actions for a task row: Add to / remove from Today, and Snooze (or Unsnooze). */
export function RowQuickActions({ task, today = true }: { task: TaskWithFlags; today?: boolean }) {
  const a = useTaskActions();
  const planned = a.isPlannedToday(task);
  if (task.status === "done" || task.status === "cancelled" || task.deletedAt) return null;
  return (
    <>
      {today && (
        <Tooltip content={planned ? "Remove from Today" : "Add to Today"} shortcut="T">
          <Button
            size="icon-sm"
            variant="quiet"
            aria-label={planned ? `Remove ${task.title} from Today` : `Add ${task.title} to Today`}
            aria-pressed={planned}
            onClick={() => void a.toggleToday(task)}
          >
            <Star className={cn(planned && "fill-[var(--star)] text-[var(--star)]")} />
          </Button>
        </Tooltip>
      )}
      {task.flags.isSnoozed ? (
        <Tooltip content="Unsnooze">
          <Button size="icon-sm" variant="quiet" aria-label={`Unsnooze ${task.title}`} onClick={() => void a.unsnooze(task)}>
            <AlarmClockOff />
          </Button>
        </Tooltip>
      ) : (
        <RowSnoozeMenu task={task} />
      )}
    </>
  );
}
